// src/hooks/useAuth.js
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { supabase } from '../utils/supabase'

const AuthContext = createContext({})

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null)
    const [profile, setProfile] = useState(null)
    const [loading, setLoading] = useState(true)
    const [initialized, setInitialized] = useState(false)

    // 使用 ref 来跟踪当前处理的用户，避免重复处理
    const currentUserIdRef = useRef(null)
    const profileLoadingRef = useRef(false)

    // 优化的 profile 加载函数
    const loadUserProfile = useCallback(async (user) => {
        // 如果正在加载相同用户的 profile，直接返回
        if (profileLoadingRef.current || currentUserIdRef.current === user.id) {
            return
        }

        profileLoadingRef.current = true
        currentUserIdRef.current = user.id

        try {
            console.log('Loading profile for user:', user.id)
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', user.id)
                .single()

            if (error && error.code !== 'PGRST116') {
                console.error('Error loading profile:', error)
                return
            }

            if (!data) {
                console.log('Profile not found, creating new profile...')
                await createUserProfile(user)
            } else {
                console.log('Profile found:', data)
                setProfile(data)
            }
        } catch (error) {
            console.error('Error loading profile:', error)
        } finally {
            profileLoadingRef.current = false
            setLoading(false)
        }
    }, [])

    const createUserProfile = useCallback(async (user) => {
        try {
            const githubUserId = user.user_metadata?.provider_id
            const githubUsername = user.user_metadata?.user_name || user.user_metadata?.preferred_username
            const avatarUrl = user.user_metadata?.avatar_url

            const profileData = {
                id: user.id,
                github_user_id: githubUserId ? parseInt(githubUserId) : null,
                github_username: githubUsername,
                avatar_url: avatarUrl
            }

            console.log('Creating profile with data:', profileData)
            const { data, error } = await supabase
                .from('profiles')
                .insert(profileData)
                .select()
                .single()

            if (error) {
                console.error('Error creating profile:', error)
                return
            }

            console.log('Profile created successfully:', data)
            setProfile(data)
        } catch (error) {
            console.error('Error creating profile:', error)
        }
    }, [])

    // 初始化认证状态
    useEffect(() => {
        let mounted = true

        const initializeAuth = async () => {
            try {
                const { data: { session }, error } = await supabase.auth.getSession()

                if (!mounted) return

                if (error) {
                    console.error('Error getting session:', error)
                    setUser(null)
                } else {
                    console.log('Initial session:', session)
                    const newUser = session?.user ?? null
                    setUser(newUser)

                    // 如果有用户且没有加载过 profile，则加载
                    if (newUser && !profileLoadingRef.current) {
                        loadUserProfile(newUser)
                    } else if (!newUser) {
                        setLoading(false)
                    }
                }
            } catch (err) {
                console.error('Error in initializeAuth:', err)
                if (mounted) {
                    setUser(null)
                    setLoading(false)
                }
            } finally {
                if (mounted) {
                    setInitialized(true)
                }
            }
        }

        initializeAuth()

        // 监听认证状态变化
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (event, session) => {
                if (!mounted) return

                console.log('onAuthStateChange event:', event, session)
                const newUser = session?.user ?? null

                // 只在用户真正改变时更新状态
                if (newUser?.id !== currentUserIdRef.current) {
                    setUser(newUser)

                    if (!newUser) {
                        // 用户登出
                        setProfile(null)
                        setLoading(false)
                        currentUserIdRef.current = null
                        profileLoadingRef.current = false
                    } else {
                        // 新用户登录，加载 profile
                        loadUserProfile(newUser)
                    }
                }
            }
        )

        return () => {
            mounted = false
            subscription.unsubscribe()
        }
    }, [loadUserProfile])

    const signInWithGitHub = useCallback(async () => {
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'github',
            options: {
                scopes: 'read:user,user:email',
                redirectTo: `${window.location.origin}`
            }
        })
        if (error) console.error('Error signing in:', error)
    }, [])

    const signOut = useCallback(async () => {
        const { error } = await supabase.auth.signOut()
        if (error) console.error('Error signing out:', error)

        // 重置所有状态
        setProfile(null)
        setUser(null)
        currentUserIdRef.current = null
        profileLoadingRef.current = false
        setLoading(false)
    }, [])

    const installGitHubApp = useCallback(() => {
        if (!user || !profile) {
            console.error('User must be logged in to install GitHub App')
            return
        }

        const state = btoa(JSON.stringify({
            userId: user.id,
            timestamp: Date.now()
        }))

        window.location.href = `https://github.com/apps/echoguard-app/installations/new?state=${state}`
    }, [user, profile])

    return (
        <AuthContext.Provider value={{
            user,
            profile,
            loading: loading || !initialized,
            signInWithGitHub,
            signOut,
            installGitHubApp
        }}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider')
    }
    return context
}
